const SUPER_ADMIN_EMAIL_HINT =
  /(^|[._-])super[._-]?admin(@|[._-])|superadmin@|@superadmin\./i;

const SENSITIVE_LOG_KEYS = new Set([
  'session_token',
  'token',
  'access_token',
  'refresh_token',
  'password',
  'owner_password',
  'owner_password_confirm',
  'new_password',
  'current_password',
  'secret',
  'api_key',
  'private_key',
]);

/** Prefer super-admin login first to avoid a misleading tenant 401 in the network tab. */
export function shouldTrySuperAdminLoginFirst(email: string): boolean {
  return SUPER_ADMIN_EMAIL_HINT.test(email.trim());
}

/** Apex (skoolfix.com) only accepts platform super-admin — school users must use their campus URL. */
export function apexPlatformLoginOnlyMessage(): string {
  return 'Only platform super-admin can sign in here. Open your school’s portal URL to sign in with a school account.';
}

/** User-safe login failure message — never echo tokens or raw API payloads. */
export function sanitizeLoginErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const msg = error.message.trim();
    if (msg === apexPlatformLoginOnlyMessage()) return msg;
  }
  return 'Invalid email or password. Use your school account for tenant login, or super admin credentials for the platform console.';
}

/** User-safe Google / Zoho OAuth failure — prefer API “not registered” copy when present. */
export function sanitizeOAuthLoginErrorMessage(error: unknown, provider = 'Google'): string {
  if (error instanceof Error) {
    const msg = error.message.trim();
    if (
      /not registered|no registered|ask your school admin|not linked to a school|verified email/i.test(
        msg
      )
    ) {
      return msg.replace(/^\s*Unauthorized\s*\(\s*401\s*\)\s*[—–-]\s*/i, '').trim() || msg;
    }
    if (/expired|already used|state mismatch|invalid google|invalid zoho/i.test(msg)) {
      return msg.replace(/^\s*(Bad Request|Unauthorized)\s*\(\s*\d+\s*\)\s*[—–-]\s*/i, '').trim() || msg;
    }
  }
  return `${provider} sign-in failed. Only users already registered by your school can sign in this way.`;
}

/** Remove secrets before writing auth payloads to the console. */
export function redactSensitiveRecord(value: unknown): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) {
    return value.map((entry) => redactSensitiveRecord(entry));
  }
  if (typeof value !== 'object') {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_LOG_KEYS.has(key.toLowerCase())) {
      out[key] = '[redacted]';
    } else {
      out[key] = redactSensitiveRecord(entry);
    }
  }
  return out;
}

export function isPublicAuthEndpoint(endpoint: string): boolean {
  return (
    endpoint === '/config' ||
    endpoint.startsWith('/auth/login') ||
    endpoint.startsWith('/auth/password-reset') ||
    endpoint.startsWith('/auth/oauth/') ||
    endpoint === '/super-admin/login'
  );
}
